import { useState, useCallback, useRef, useEffect } from "react";

const SkeletonPlaceholder = ({ variant = "light" }) => (
  <span
    className={`media-skeleton media-skeleton--${variant}`}
    aria-hidden="true"
  />
);

export const SkeletonImage = ({
  src,
  alt = "",
  className = "",
  wrapperClassName = "",
  wrapperStyle,
  loading = "lazy",
  aspectRatio,
  skeletonVariant = "light",
  ...imgProps
}) => {
  const imgRef = useRef(null);
  const [loaded, setLoaded] = useState(false);

  const markLoaded = useCallback(() => setLoaded(true), []);

  useEffect(() => {
    const img = imgRef.current;
    if (img?.complete && img.naturalHeight > 0) {
      setLoaded(true);
    }
  }, [src]);

  const wrapStyle = {
    ...(aspectRatio ? { aspectRatio } : {}),
    ...wrapperStyle,
  };
  const hasWrapStyle = Object.keys(wrapStyle).length > 0;

  return (
    <span
      className={`media-skeleton-wrap ${loaded ? "is-loaded" : ""} ${wrapperClassName}`.trim()}
      style={hasWrapStyle ? wrapStyle : undefined}
    >
      {!loaded && <SkeletonPlaceholder variant={skeletonVariant} />}
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        className={`media-skeleton-media ${className}`.trim()}
        loading={loading}
        decoding="async"
        onLoad={markLoaded}
        onError={markLoaded}
        {...imgProps}
      />
    </span>
  );
};

export const SkeletonVideo = ({
  className = "",
  wrapperClassName = "",
  fill = false,
  aspectRatio,
  skeletonVariant = "light",
  children,
  ...videoProps
}) => {
  const videoRef = useRef(null);
  const [loaded, setLoaded] = useState(false);

  const markLoaded = useCallback(() => setLoaded(true), []);

  useEffect(() => {
    const video = videoRef.current;
    if (video && video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      setLoaded(true);
    }
  }, []);

  const wrapClass = [
    "media-skeleton-wrap",
    loaded ? "is-loaded" : "",
    fill ? "media-skeleton-wrap--fill" : "",
    wrapperClassName,
  ]
    .filter(Boolean)
    .join(" ");

  const wrapStyle = aspectRatio && !fill ? { aspectRatio } : undefined;

  return (
    <span className={wrapClass} style={wrapStyle}>
      {!loaded && <SkeletonPlaceholder variant={skeletonVariant} />}
      <video
        ref={videoRef}
        className={`media-skeleton-media ${className}`.trim()}
        preload={videoProps.preload ?? "metadata"}
        onCanPlay={markLoaded}
        onLoadedData={markLoaded}
        onError={markLoaded}
        {...videoProps}
      >
        {children}
      </video>
    </span>
  );
};
